import type { ReactNode } from 'react';

export type TimelineTone = 'neutral' | 'accent' | 'info' | 'success' | 'warning' | 'danger';

export interface TimelineGroupProps {
  /** Date / section label rendered above its items (e.g. `"Today"`). */
  label: ReactNode;
  children?: ReactNode;
  className?: string;
}

export interface TimelineItemProps {
  /** Body content — title + optional details, metadata, links. */
  children: ReactNode;
  /** Leading icon. Omit to render just a coloured dot. */
  icon?: ReactNode;
  /** Dot colour channel. Default `neutral`. */
  tone?: TimelineTone;
  /** Timestamp / meta shown on the right. */
  time?: ReactNode;
  /** Mark as the "now" pulse — subtle halo around the dot. */
  active?: boolean;
  className?: string;
}

export interface TimelineProps {
  /** Children are TimelineGroup and/or TimelineItem nodes. */
  children?: ReactNode;
  className?: string;
}

/** Vertical activity / revision log. Items hang off a continuous left
 *  rail; coloured dots segment the flow. Optional groups insert a date
 *  heading ("Today / Yesterday / Earlier this week").
 *
 *  Not a feed — content is composed, not virtualized. For long feeds
 *  (1000+ items), pair with a virtualizer once that primitive ships. */
function TimelineRoot(props: TimelineProps): ReactNode {
  const { children, className } = props;
  return (
    <div className={['vq-timeline', className].filter(Boolean).join(' ')} data-component="Timeline">
      {children}
    </div>
  );
}

function TimelineGroup(props: TimelineGroupProps): ReactNode {
  const { label, children, className } = props;
  return (
    <section className={['vq-timeline__group', className].filter(Boolean).join(' ')}>
      <div className="vq-timeline__date">{label}</div>
      <div className="vq-timeline__items">{children}</div>
    </section>
  );
}

function TimelineItem(props: TimelineItemProps): ReactNode {
  const { children, icon, tone = 'neutral', time, active, className } = props;
  const classes = ['vq-timeline__item', active === true && 'vq-timeline__item--active', className]
    .filter(Boolean)
    .join(' ');
  return (
    <div className={classes} data-tone={tone}>
      <span className="vq-timeline__rail" aria-hidden="true" />
      <span className="vq-timeline__marker" data-tone={tone} aria-hidden="true">
        {icon}
      </span>
      <div className="vq-timeline__body">
        <div className="vq-timeline__content">{children}</div>
        {time !== undefined && <div className="vq-timeline__time">{time}</div>}
      </div>
    </div>
  );
}

interface TimelineCompound {
  (props: TimelineProps): ReactNode;
  Group: typeof TimelineGroup;
  Item: typeof TimelineItem;
}

export const Timeline: TimelineCompound = Object.assign(TimelineRoot, {
  Group: TimelineGroup,
  Item: TimelineItem,
});
